import { Box } from "@mui/material"
import { Property } from "csstype"
import React, { ReactNode, useMemo } from "react"
import { shallowEqual } from "react-redux"
import type { AnySceneObjectT } from "tweenly"
import { renderStyles } from "tweenly"
import { selectUndoable } from "../../../../helpers/selector.helpers"
import { useDraggable } from "../../../../hooks/editor/useDraggable"
import useHoldDrag, { dragNodeRefKey } from "../../../../hooks/editor/useHoldDrag"
import useWrapSelectedObject from "../../../../hooks/editor/useWrapSelectedObject"
import useDynamicRefs from "../../../../hooks/useDynamicRefs"
import { useAppDispatch, useAppSelector } from "../../../../hooks/useRedux"
import {
    selectIsObjectSelected,
    selectSelectionCount,
} from "../../../../store/slices/activeObject.slice"
import { CanvasCursor } from "../../../../store/slices/canvasCursor.slice"
import { selectObjectsByIds } from "../../../../store/slices/objects.slice"

import { useEditingTextMode } from "../../../../hooks/editor/useEditingTextMode"
import { setIsEditingTextMode } from "../../../../store/slices/editor.slice"
import CanvasItem from "../CanvasItem"
import { SELECTION_OVERLAY_REF_KEY } from "../SelectionOverlay"
import EditableText from "./EditableText"

interface Props {
    object: AnySceneObjectT
    isHiding: boolean
    isLocking: boolean
    isRootTicker: boolean
    canvasScaleCoef: number
    borderCoef: number
    handleSelectObject: (
        event: React.MouseEvent,
        objectId: string,
        animationIds: string[],
        multiSelectKey: boolean
    ) => void
    children?: ReactNode
    visibility?: Property.Visibility | undefined
    className: string
    style?: React.CSSProperties
}

const AbstractContainer = ({
    object,
    isHiding,
    isLocking,
    isRootTicker,
    canvasScaleCoef,
    borderCoef,
    handleSelectObject,
    children,
    visibility,
    className,
    style,
}: Props) => {
    const dispatch = useAppDispatch()
    // Narrow subscriptions instead of the whole selection array: switching between two single
    // selections then re-renders only the two objects involved, not all ~200 on the canvas.
    const isSelected = useAppSelector((state) => selectIsObjectSelected(state, object.id))
    const selectionCount = useAppSelector(selectSelectionCount)
    // only this object's direct children, compared shallowly: a change to any other object
    // leaves these references untouched, so the ~200 containers that are not involved bail
    // out instead of re-rendering (this fired on every mouse-up at the end of a drag)
    const childObjects = useAppSelector(
        (state) => selectObjectsByIds(selectUndoable(state).objects, object.childIds),
        shallowEqual
    )

    const canvasCursor = useAppSelector((state) => state.canvasCursor.value)

    // renderStyles walks every style of the object and builds a fresh CSS object; during a
    // drag this container re-renders on every mousemove
    const objectStyles: React.CSSProperties = useMemo(
        () => renderStyles(object, "inline"),
        [object]
    )

    // Check if this object is a flex child (position: static means it's in a flexbox layout)
    const isFlexChild = object.styles.find((s) => s.property === "position")?.value === "static"

    const isLocked = isLocking || object.isLocked

    const [getRef, setRef] = useDynamicRefs<HTMLDivElement>()

    const {
        handleOnStartDrag,
        handleDragAxisChange,
        handleDragEnd,
        getSnapSession,
        dragging,
        defaultSingleWrapperState,
        draggableScale,
        setCursorWhenDragging,
    } = useDraggable(object, canvasScaleCoef, undefined)

    /**
     * Published into the ref registry instead of a local ref, so a multiselection can move
     * this object without re-parenting it — see `dragNodeRefKey`.
     */
    const registerDragNode = setRef(dragNodeRefKey(object.id))

    //scoped to this object: only the selected one actually draws the wrapper box
    const { wrapperObjectsStyles } = useWrapSelectedObject(object.id)

    const isEditingTextMode = useAppSelector((state) => state.editor.value.isEditingTextMode)
    const { setIsEditingMode } = useEditingTextMode()

    /**
     * Dragging is a property of the SELECTION, not of the object under the cursor: you select
     * with a click and then move what you selected by holding. Exactly one container can
     * satisfy this at a time, so only one canvas-wide pointer listener is ever live.
     */
    useHoldDrag({
        enabled:
            isSelected &&
            selectionCount === 1 &&
            !isLocked &&
            !isRootTicker &&
            !isFlexChild &&
            !isEditingTextMode &&
            canvasCursor !== CanvasCursor.GrabCursorBySpace,
        scale: draggableScale,
        getDragNodes: () => [getRef(dragNodeRefKey(object.id))?.current ?? null],
        // the outline is what the user aligns against another object, so it goes away for
        // the duration of the move (GE-1649)
        getNodesToHide: () => [getRef(SELECTION_OVERLAY_REF_KEY)?.current ?? null],
        /**
         * The hit stack contains every element under the pointer — including the ones another
         * object is painted over — but only those whose OWN box is under it. A part of this
         * object that reaches outside its box (a line's wider hit area, see LineContainer)
         * shows up without the object's element, so the match walks up from each element:
         * a press on any part or child of this object counts as a press on the object.
         */
        isHit: (stack) =>
            stack.some((element) => {
                for (let node: Element | null = element; node; node = node.parentElement) {
                    if (node.id === object.id) return true
                }
                return false
            }),
        onDragStart: handleOnStartDrag,
        onAxisChange: handleDragAxisChange,
        getSnapSession,
        onDragEnd: handleDragEnd,
    })

    const tempDisableDoubleTextEdit = true
    const handleClick = (e: React.MouseEvent) => {
        if (object.type === "text") {
            if (
                isEditingTextMode ||
                (isLocking && !isRootTicker) ||
                (object.isLocked && !isRootTicker)
            ) {
                return
            }
            handleSelectObject(e, object.id, Object.keys(object.animations), e.shiftKey)

            if (!tempDisableDoubleTextEdit) {
                if (!isRootTicker) {
                    setIsEditingMode()
                }
            }
        } else {
            dispatch(setIsEditingTextMode(false))
            handleSelectObject(e, object.id, Object.keys(object.animations), e.shiftKey)
        }
    }

    // A flex child is positioned by its parent's layout, so it is never dragged — no drag
    // node, and `useHoldDrag` above is disabled for it.
    if (isFlexChild) {
        // For flex children, remove top/left positioning - flexbox handles it
        const { top: _top, left: _left, ...flexChildStyles } = objectStyles

        return (
            <div
                className={`${className} ` + object.id}
                id={object.id}
                ref={setRef(object.id)}
                onClick={handleClick}
                style={{
                    ...style,
                    ...flexChildStyles,
                    boxSizing: "border-box", // Include padding in width/height
                    visibility: visibility,
                    // SelectionOverlay draws the outline; it measures the rendered element,
                    // so it frames a flex child correctly even though the object's own
                    // top/left styles say nothing about where flexbox put it
                    cursor: "pointer",
                    pointerEvents: isLocked ? "none" : "auto",
                }}
            >
                {children}
                {object.childIds.map((childId: string, index: number) => (
                    <CanvasItem
                        key={childId}
                        object={childObjects[index]}
                        canvasScaleCoef={canvasScaleCoef}
                        isHiding={(object.isHidden ? object.isHidden : false) || isHiding}
                        isLocking={object.isLocked || isLocking}
                        isRootTicker={isRootTicker}
                        borderCoef={borderCoef}
                    />
                ))}
            </div>
        )
    }

    // Normal rendering for absolute positioned elements. Moving them is the hold gesture's
    // job (see useHoldDrag), which writes its preview transform straight to the drag node —
    // that node must therefore stay free of any style React manages.
    return (
        <Box
            style={{
                cursor: "pointer",
                zIndex: object.index,
                pointerEvents: isLocked ? "none" : "auto",
            }}
            onClick={(e) => {
                e.stopPropagation()
            }}
        >
            {/**
             * `position: relative` makes this node the containing block of the absolutely
             * positioned object below it. react-draggable used to achieve that as a side
             * effect of always rendering `transform: translate(0,0)` here; without it the
             * object would resolve its top/left against a different ancestor at rest and
             * jump the moment the drag applied its first transform.
             *
             * It also keeps the two writers apart: React owns `position`, the gesture owns
             * `transform`, so neither can undo the other on a re-render.
             */}
            <Box ref={registerDragNode} style={{ position: "relative" }}>
                <div
                    className={`${className} ` + object.id}
                    id={object.id}
                    ref={setRef(object.id)}
                    onClick={handleClick}
                    style={{
                        ...style,
                        ...objectStyles,
                        visibility: visibility,
                        // the selection outline is drawn by SelectionOverlay, above every
                        // object, so it stays visible when something covers the selection
                        cursor: setCursorWhenDragging,
                    }}
                >
                    {children}
                    {object.childIds.map((childId: string, index: number) => (
                        <CanvasItem
                            key={childId}
                            object={childObjects[index]}
                            canvasScaleCoef={canvasScaleCoef}
                            isHiding={(object.isHidden ? object.isHidden : false) || isHiding}
                            isLocking={object.isLocked || isLocking}
                            isRootTicker={isRootTicker}
                            borderCoef={borderCoef}
                        />
                    ))}
                </div>

                {/**
                 * Host for the inline text editor, nothing more.
                 *
                 * It used to be the visible selection box too — a translucent fill with a
                 * dotted outline around the object AND its children. SelectionOverlay draws
                 * the selection now, so this box only has to supply the geometry the editor
                 * is positioned against, and it stays out of the pointer's way.
                 */}
                {object.type === "text" &&
                    isEditingTextMode &&
                    object.id === wrapperObjectsStyles.objectId &&
                    (defaultSingleWrapperState || dragging) && (
                        <Box
                            sx={{
                                transformOrigin: "top left",
                                top: wrapperObjectsStyles.top,
                                left: wrapperObjectsStyles.left,
                                height: wrapperObjectsStyles.height,
                                width: wrapperObjectsStyles.width,
                                position: "absolute",
                                zIndex: -10,
                                // the text field inside has to stay typeable
                                pointerEvents: "auto",
                            }}
                        >
                            <EditableText objectId={object.id} initialText={children as string} />
                        </Box>
                    )}
            </Box>
        </Box>
    )
}

export default AbstractContainer
